import { Language } from "./i18n"
import { sanitizeHtml } from "./sanitize"
import glossaryHi from "./glossary.hi"
import glossaryPa from "./glossary.pa"
import { authorizedHi } from "./allpanlanc.hi"
import { authorizedPa } from "./allpanlanc.pa"
import { getProtectedAdminSet } from "./translation-guard"

// Reserved for future use (e.g., case preservation, glossary overrides)

const env = (import.meta as any).env || {}
const API_ENDPOINT: string | undefined = env.VITE_TRANSLATE_ENDPOINT
const API_KEY: string | undefined = env.VITE_TRANSLATE_API_KEY

async function apiTranslateBatch(texts: string[], targetLang: Language): Promise<string[]> {
  if (!API_ENDPOINT || !API_KEY) return texts
  try {
    const res = await fetch(API_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${API_KEY}`,
      },
      body: JSON.stringify({ q: texts, target: targetLang }),
    })
    if (!res.ok) throw new Error(`Translate API ${res.status}`)
    const data = await res.json()
    const out: string[] = data?.data?.translations?.map((t: any) => t?.translatedText) ?? texts
    return out
  } catch {
    return texts
  }
}

function localTranslate(text: string, lang: Language): string {
  const protectedSet = getProtectedAdminSet()
  const skipToken = (token: string) => protectedSet.has(token.trim())

  if (lang === "hi") {
    let out = text
    Object.entries(glossaryHi).forEach(([en, hi]) => {
      if (skipToken(en)) return
      const re = new RegExp(`\\b${escapeRegExp(en)}\\b`, "gi")
      out = out.replace(re, hi)
    })
    authorizedHi.forEach((hi) => {
      if (!hi || skipToken(hi)) return
      // No-op: authorized list is for validation; replacements rely on glossary mapping
    })
    // Normalize punctuation for Hindi lists: replace trailing semicolons with danda
    out = out.replace(/;(?=\s|$)/g, "।")
    out = out.replace(/\s+/g, (m) => (m.length > 1 ? " " : m))
    return out
  }
  if (lang === "pa") {
    let out = text
    Object.entries(glossaryPa).forEach(([en, pa]) => {
      if (skipToken(en)) return
      const re = new RegExp(`\\b${escapeRegExp(en)}\\b`, "gi")
      out = out.replace(re, pa)
    })
    authorizedPa.forEach((pa) => {
      if (!pa || skipToken(pa)) return
    })
    // Normalize punctuation for Punjabi readability in list items and content
    // Convert semicolons commonly used at end of bullet items to danda
    out = out.replace(/;(?=\s|$)/g, "।")
    out = out.replace(/\s+/g, (m) => (m.length > 1 ? " " : m))
    return out
  }
  return text
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

export async function translateHtml(html: string, targetLang: Language): Promise<string> {
  const safe = sanitizeHtml(html)
  const tpl = document.createElement("template")
  tpl.innerHTML = safe

  const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []
  while (walker.nextNode()) {
    const node = walker.currentNode as Text
    const parentTag = (node.parentElement?.tagName || "").toLowerCase()
    if (parentTag === "script" || parentTag === "style") continue
    const content = node.nodeValue || ""
    if (!content.trim()) continue
    textNodes.push(node)
  }

  const originals = textNodes.map((n) => n.nodeValue || "")
  let translated = originals.map((t) => localTranslate(t, targetLang))

  // Attempt API translation when configured, feeding the locally-processed text as base
  translated = await apiTranslateBatch(translated, targetLang)

  textNodes.forEach((n, i) => {
    n.nodeValue = translated[i]
  })

  // Translate common attributes: alt, title, aria-label
  const attrNames = ["alt", "title", "aria-label"]
  const attrElements: Array<{ el: Element; attr: string; value: string }> = []
  tpl.content.querySelectorAll("*").forEach((el) => {
    attrNames.forEach((attr) => {
      const val = el.getAttribute(attr)
      if (val && val.trim()) {
        attrElements.push({ el, attr, value: val })
      }
    })
  })
  if (attrElements.length) {
    let attrTranslated = attrElements.map((a) => localTranslate(a.value, targetLang))
    attrTranslated = await apiTranslateBatch(attrTranslated, targetLang)
    attrElements.forEach((a, i) => {
      a.el.setAttribute(a.attr, attrTranslated[i])
    })
  }

  return tpl.innerHTML
}
